import { Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';

import { VerificationDetailProps } from 'dashboard/components/VerificationDetail/types';
import ChecksTableDatabaseTin from 'dashboard/components/VerificationDetail/VerificationTypes/DatabaseTinVerification/ChecksTableDatabaseTin';
import { titleCase } from 'dashboard/lib/utils/stringFormat';
import { VerificationDatabaseTin } from 'dashboard/models/verification';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import { Heading } from 'design-system/components/Text';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';
import { VerificationStatus } from 'lib/verification';

const StyledAttributeGrid = styled(AttributeGrid)`
  padding: 0 ${Spacing[4]} ${Spacing[2]};
`;

const ChecksInformationRow = styled('div')`
  display: grid;
  grid-template-columns: auto;
  grid-row-gap: ${Spacing[4]};
`;

interface DatabaseVerificationTinProps extends VerificationDetailProps {
  verification: VerificationDatabaseTin;
}

const ChecksHeaderContainer = styled('div')`
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  padding: 0 ${Spacing[3]};
`;

const TitleContainer = styled('div')`
  justify-self: start;
`;

const ChecksHeader = ({ title }: { title: string }) => {
  return (
    <ChecksHeaderContainer>
      <TitleContainer>
        <Heading size='sm'>{title}</Heading>
      </TitleContainer>
    </ChecksHeaderContainer>
  );
};

const DatabaseTinVerification = ({ verification }: DatabaseVerificationTinProps) => {
  const {
    tin: taxIdentificationNumber,
    'name-first': nameFirst,
    'name-last': nameLast,
    'name-business': nameBusiness,
    'tin-type': tinType,
    'redacted-at': redactedAt,
  } = verification.attributes;

  const isRedacted = redactedAt != null;

  const submitted = status !== VerificationStatus.Initiated;

  const checks = verification.attributes.checks;

  const permissionedTIN = usePermission(Permission.VerificationDatabaseSsnView)
    ? taxIdentificationNumber
    : taxIdentificationNumber?.substring(taxIdentificationNumber?.length - 4);

  return (
    <ChecksInformationRow>
      <ChecksHeader title='Collected Fields' />
      <StyledAttributeGrid>
        {nameFirst && (
          <Attribute
            label='First Name'
            value={titleCase(nameFirst)}
            redacted={isRedacted}
            placeholder='No first name collected'
            data-test='database-tin-verification__first-name'
          />
        )}
        {nameLast && (
          <Attribute
            label='Last Name'
            value={titleCase(nameLast)}
            redacted={isRedacted}
            placeholder='No last name collected'
            data-test='database-tin-verification__last-name'
          />
        )}
        {nameBusiness && (
          <Attribute
            label='Business Name'
            value={titleCase(nameBusiness)}
            redacted={isRedacted}
            placeholder='No birthdate collected'
            data-test='database-tin-verification__birthdate'
          />
        )}
        {tinType && (
          <Attribute
            label='Detected TIN Type'
            value={tinType}
            redacted={isRedacted}
            placeholder='TIN Type not determined'
            data-test='database-tin-verification__tin-type'
          />
        )}
        <Attribute
          label='TIN'
          value={permissionedTIN}
          redacted={isRedacted}
          placeholder='No TIN collected'
          toggleable
        />
      </StyledAttributeGrid>
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      {submitted && <ChecksTableDatabaseTin checks={checks} />}
    </ChecksInformationRow>
  );
};

export default DatabaseTinVerification;
